import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React, { memo } from "react";
import { type NodeProps } from "@xyflow/react";
import type { ExplorerNode } from "../../declarations/IExplorerTypes";
import NodeListCard from "./NodeListCard";
import NodeSkeleton from "./NodeSkeleton";
import NodeToolbar from "./NodeToolbar";
import { getNodeColor, getNodeIcon } from "./nodeColors";

function formatAmount(value: number, currency: string): string {
    const abs = Math.abs(value);
    return `${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function formatDate(iso: string): string {
    if (!iso) {
        return "—";
    }
    return iso.slice(0, 10);
}

function CustomerPaymentsNode({ data }: NodeProps<ExplorerNode>) {
    const isLoading = data.status === "loading";
    const nodeColor = getNodeColor(data.entityType);
    const payments = data.payments ?? [];
    const totalLabel = payments.length > 0 && data.paymentsCurrency != null && data.paymentsTotal != null
        ? formatAmount(data.paymentsTotal, data.paymentsCurrency)
        : null;

    return (
        <NodeListCard
            nodeColor={nodeColor}
            isSelected={data.isSelected}
            isLoading={isLoading}
            width={240}
        >
            <NodeToolbar
                nodeColor={nodeColor}
                icon={getNodeIcon(data.entityType)}
                title={data.nodeTitle}
                subtitle={data.nodeSubtitle}
                dataAreaId={data.dataAreaId}
                onInfoClick={data.onSelect ? () => data.onSelect!(data.selectId ?? data.entityType) : undefined}
            />

            {isLoading ? (
                <NodeSkeleton rows={3} variant="list" />
            ) : payments.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                    No payments found
                </Typography>
            ) : (
                <>
                    {totalLabel && (
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5, mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                {payments.length} payment{payments.length === 1 ? "" : "s"}
                            </Typography>
                            <Typography variant="caption" fontWeight={600} sx={{ color: nodeColor }}>
                                {totalLabel}
                            </Typography>
                        </Stack>
                    )}
                    <List dense disablePadding sx={{ mt: 0.5 }}>
                        {payments.map((p, index) => (
                            <React.Fragment key={p.voucher}>
                                {index > 0 && <Divider sx={{ mx: 0.5, opacity: 0.4 }} />}
                                <ListItem dense disableGutters sx={{ py: 0.25, px: 0.5 }}>
                                    <ListItemText
                                        disableTypography
                                        primary={
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Typography variant="caption" fontWeight={500} sx={{ lineHeight: 1.3 }}>
                                                    {p.voucher}
                                                </Typography>
                                                <Box sx={{ flex: 1 }} />
                                                <Typography variant="caption" sx={{ lineHeight: 1.3, color: "text.secondary" }}>
                                                    {formatDate(p.transDate)}
                                                </Typography>
                                            </Stack>
                                        }
                                        secondary={
                                            <Typography variant="caption" sx={{ lineHeight: 1.2, color: "text.secondary" }}>
                                                {formatAmount(p.amountCur, p.currencyCode)}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            </React.Fragment>
                        ))}
                    </List>
                </>
            )}
        </NodeListCard>
    );
}

export default memo(CustomerPaymentsNode);
